import { GlobalComposable } from "~/classes/composable/GlobalComposable"

/**
 * These are not camel cased!!
 */
export interface OauthParameters {
  redirect_uri: string
  client_id?: string
  code_challenge?: string
  state?: string
  scopes?: string
}

interface State {
  email: string
  password: string
  inviteCode: string
  newPassword: string
  verificationCode: string
  userId: string
}
class Authentication extends GlobalComposable<State> {
  override defaultState = () => {
    return {
      email: "",
      password: "",
      newPassword: "",
      inviteCode: "",
      verificationCode: "",
      userId: "",
    }
  }

  isOnAuthPage = () => {
    const { path } = useRoute()
    return path === "/login" || path === "/signup" || path === "/verify"
  }

  onAuthLayoutMounted = () => {
    const { initializeWorkers } = useNavigation()
    initializeWorkers()
  }

  /**
   * This technically uses the cache state but is more logically placed here.
   * This checks for whether we are an anonymous user either offline or online.
   */
  readonly isAnonymous = () => {
    const { cacheState } = useCache()
    return isDefined(cacheState.value.anonId)
  }

  /**
   * This technically uses the cache state but is more logically placed here
   * This checks for whether we are a logged in user either offline or online.
   */
  readonly isAuthenticated = () => {
    const { cacheState } = useCache()
    return isDefined(cacheState.value.userId)
  }

  authCode = async (query: OauthParameters) => {
    const { api } = useCache()
    if (isDesktop())
      throw new Error("Cannot do auth login on desktop")
    return await navigateTo({
      path: api("/auth/authorization-code"),
      query: query as any,
    }, { external: true })
  }

  signUp = async (request: SignupRequest) => {
    const { api } = useCache()
    const { signup } = useTauri()

    let response: SignupResponse
    if (isDesktop())
      response = await signup(request)
    else
      response = await $fetch<SignupResponse>(api("/auth/signup"), {
        method: "POST",
        body: request,
      })
    this.debug("Sign-up response: ", response)
    this.state.value.userId = response.userId
  }

  verify = async (request: VerifyRequest) => {
    const { initializeWorkers } = useNavigation()
    const { api, cacheState, sendCacheLogin } = useCache()
    const { identify } = useAnalytics()
    const { waitForFirstResponse } = useEvents()
    const { verify } = useTauri()

    let response: LoginResponse
    if (isDesktop())
      response = await verify(request)
    else
      response = await $fetch<LoginResponse>(api("/auth/verify"), {
        method: "POST",
        body: request,
      })

    cacheState.value.userId = response.userId
    identify(this.state.value.userId, this.state.value.email)

    const promise = waitForFirstResponse("didConnectAuthStatus")
    await initializeWorkers()
    sendCacheLogin(response.userId)
    await promise
  }

  resendVerification = async (request: NewVerificationCodeRequest) => {
    const { api } = useCache()
    const { resendVerification } = useTauri()
    if (isDesktop())
      await resendVerification(request)
    else
      await $fetch<LoginResponse>(api("/auth/resend-verification"), {
        method: "POST",
        body: request,
      })
    this.debug("Resent verification code")
  }

  logIn = async (request: LoginRequest) => {
    const { api, cacheState, sendCacheLogin } = useCache()
    const { login } = useTauri()
    const { identify } = useAnalytics()
    const { initializeWorkers } = useNavigation()
    const { waitForFirstResponse } = useEvents()

    let response: LoginResponse
    if (isDesktop())
      response = await login(request)
    else
      response = await $fetch<LoginResponse>(api("/auth/login"), {
        method: "POST",
        body: request,
      })
    this.debug("Logged in!", response)
    cacheState.value.userId = response.userId
    identify(response.userId, this.state.value.email)

    const promise = waitForFirstResponse("didConnectAuthStatus")
    await initializeWorkers()
    sendCacheLogin(response.userId)
    await promise
  }

  logOut = async () => {
    const { api, sendCacheLogout, cacheState } = useCache()
    const { logout } = useTauri()
    const { resetFileSystemNavigationState, navigateToLoginScreenAndClearCredentials } = useNavigation()

    if (cacheState.value.userId)
      sendCacheLogout(cacheState.value.userId)
    else    
      this.error("No stored user id, did you ever login?")

    navigateToLoginScreenAndClearCredentials()
    resetFileSystemNavigationState()

    // If this fails, we will at least have deleted client data from above
    if (isDesktop())
      await logout()
    else
      await $fetch(api("/auth/logout"), { method: "POST" })
    this.debug("Logged out")
  }

  forgotPassword = async (request: ForgotPasswordRequest) => {
    const { api } = useCache()
    const { forgotPassword } = useTauri()

    if (isDesktop())
      await forgotPassword(request)
    else
      await $fetch(api("/auth/forgot-password"), {
        method: "POST",
        body: request,
      })
    this.debug("Sent forgot password request")
    await navigateTo("/change-password")
  }

  changePassword = async (request: ChangePasswordRequest) => {
    const { waitUntilAppLoaded } = useNavigation()
    const { api, cacheState, sendCacheLogin } = useCache()
    const { identify } = useAnalytics()
    const { changePassword } = useTauri()
    const { addInterfaceEventListener, removeInterfaceEventListener } = useEvents()

    let response: LoginResponse
    if (isDesktop())
      response = await changePassword(request)
    else
      response = await $fetch<LoginResponse>(api("/auth/change-password"), {
        method: "POST",
        body: request,
      })
    this.debug("Changed password and logged in!", response)
    // Don't set cache state since we might already be in the process of connecting
    cacheState.value.userId = response.userId
    identify(response.userId, this.state.value.email)

    await waitUntilAppLoaded()
    if (cacheState.value.userId) {
      if (cacheState.value.userId !== response.userId)
        throw new Error("Already logged in as another user??")
      if (cacheState.value.userId === response.userId)
        throw new Error("Already logged in as ourselves???")
    }

    const { promise, resolve } = Promise.withResolvers<any>()
    addInterfaceEventListener("didConnectAuthStatus", resolve)
    sendCacheLogin(response.userId)
    await promise
    removeInterfaceEventListener("didConnectAuthStatus", resolve)

    this.clearState()

    await navigateTo("/home")
  }

  toReadableError = (error: any) => {
  // On Tauri, the error will just be a string
    if (typeof error === "string")
      error = { statusCode: 401, data: error }
    if (!("statusCode" in error))
      return "An unknown error occurred"
    const status = error.statusCode
    if (status === 422)
      return "Please check the formatting of your input"
    const data: string = error.data || ""
    if (data.startsWith("4002"))
      return "That email is unverified, please sign up again"
    if (data.startsWith("4003"))
      return "Your password is incorrect, try again"
    if (data.startsWith("4004"))
      return "That e-mail is not registered"
    if (data.startsWith("4005"))
      return "That e-mail already exists, try logging in"
    if (data.startsWith("4006"))
      return "Your invite code is invalid"
    if (data.startsWith("4007"))
      return "The new password entered is invalid"
    if (data.startsWith("4008"))
      return "That e-mail is already verified, try logging in"
    return "Something went wrong, try again or contact us"
  }
}

const authentication = new Authentication("authentication", theme.colors.pink.hex)
export default function () {
  return authentication.use()
}
